import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarIcon, Users, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { createQuoteRequest } from "@/lib/api";
import { api } from "@/lib/api";
import DestinationAutocomplete from "@/components/DestinationAutocomplete";

const TRIP_TYPES = ["Lune de miel", "Famille", "Couple", "Solo", "Affaires", "Anniversaire"];

const TRIP_TYPE_MAP = {
  Honeymoon: "Lune de miel",
  Famille: "Famille",
  Couple: "Couple",
  Luxe: "Couple",
  "City Break": "Couple",
  Plage: "Couple",
  "Road Trip": "Famille",
  Aventure: "Solo",
  "Bien-être": "Couple",
  Business: "Affaires",
};

const MEAL_PLANS = [
  { value: "RO", label: "RO — Sans repas (Room Only)" },
  { value: "BB", label: "BB — Petit-déjeuner (Bed & Breakfast)" },
  { value: "DP", label: "Demi-pension" },
  { value: "PC", label: "Pension complète" },
  { value: "AI", label: "All Inclusive" },
];

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  destination: "",
  departure_city: "",
  adults: 2,
  children: 0,
  budget: "",
  currency: "EUR",
  trip_type: "",
  meal_plan: "",
  flights: "",
  transfers: "",
  message: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initialState);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const bumpAdults = (d) => setField("adults", Math.max(1, Math.min(20, form.adults + d)));
  const bumpKids = (d) => setField("children", Math.max(0, Math.min(20, form.children + d)));

  // Concierge prefill on mount + on event
  useEffect(() => {
    const apply = (data) => {
      if (!data) return;
      setForm((s) => ({
        ...s,
        destination: data.destination || s.destination,
        adults: data.adults || s.adults,
        children: data.children ?? s.children,
        budget: data.budget != null ? String(data.budget) : s.budget,
        currency: data.currency || s.currency,
        trip_type: TRIP_TYPE_MAP[data.trip_type] || s.trip_type,
        message: data.preferences
          ? (s.message ? s.message + "\n" : "") + `Note du concierge : ${data.preferences}${data.period ? ` · Période : ${data.period}` : ""}`
          : s.message,
      }));
      toast.success("Vos informations ont été reportées dans le formulaire.");
    };
    // initial load from storage
    try {
      const raw = localStorage.getItem("dt_quote_prefill");
      if (raw) {
        apply(JSON.parse(raw));
        localStorage.removeItem("dt_quote_prefill");
      }
    } catch { /* noop */ }
    const handler = (e) => apply(e.detail);
    window.addEventListener("dt:prefill", handler);
    return () => window.removeEventListener("dt:prefill", handler);
  }, []);

  // Form -> Concierge sync (debounced)
  useEffect(() => {
    const sid = localStorage.getItem("dt_concierge_sid");
    if (!sid) return;
    const t = setTimeout(() => {
      const fields = {
        destination: form.destination || null,
        adults: form.adults,
        children: form.children,
        budget: form.budget ? parseFloat(form.budget) : null,
        currency: form.currency,
        trip_type: form.trip_type || null,
        meal_plan: form.meal_plan || null,
        flights: form.flights || null,
        transfers: form.transfers || null,
        full_name: form.full_name || null,
        email: form.email || null,
        phone: form.phone || null,
        check_in: range.from ? format(range.from, "yyyy-MM-dd") : null,
        check_out: range.to ? format(range.to, "yyyy-MM-dd") : null,
      };
      api.post("/concierge/sync", { session_id: sid, fields }).catch(() => { /* silent */ });
    }, 1200);
    return () => clearTimeout(t);
  }, [form, range]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!range.from || !range.to) {
      toast.error("Veuillez choisir vos dates de séjour.");
      return;
    }
    if (!form.full_name || !form.email || !form.destination || !form.budget) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        budget: parseFloat(form.budget),
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        trip_type: form.trip_type || null,
        meal_plan: form.meal_plan || null,
        flights: form.flights || null,
        transfers: form.transfers || null,
        departure_city: form.departure_city || null,
        phone: form.phone || null,
        message: form.message || null,
      };
      const data = await createQuoteRequest(payload);
      toast.success("Demande envoyée — un conseiller vous répond sous 24h.", {
        description: `Référence : ${data.id.slice(0, 8).toUpperCase()}`,
      });
      setForm(initialState);
      setRange({ from: undefined, to: undefined });
    } catch (err) {
      const data = err?.response?.data;
      let msg = "Une erreur est survenue. Réessayez.";
      if (data?.detail) {
        if (typeof data.detail === "string") msg = data.detail;
        else if (Array.isArray(data.detail) && data.detail[0]?.msg) {
          msg = data.detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ");
        }
      } else if (err?.message) {
        msg = `Erreur réseau : ${err.message}`;
      }
      console.error("Quote submit error:", err?.response || err);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="quote-form"
      className="bg-white border border-[#EAE8E3] p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7"
    >
      <div className="md:col-span-2 flex items-end justify-between border-b border-[#EAE8E3] pb-6">
        <div>
          <p className="label-eyebrow">Devis sur-mesure</p>
          <h3 className="font-serif-display text-3xl md:text-4xl mt-3">Composez votre séjour</h3>
        </div>
        <p className="hidden md:block text-xs text-[#737373] tracking-wider">Réponse sous 24h</p>
      </div>

      {/* Destination */}
      <div className="space-y-3">
        <Label htmlFor="destination" className="label-eyebrow">Destination *</Label>
        <DestinationAutocomplete
          value={form.destination}
          onChange={(v) => setField("destination", v)}
          placeholder="Commencez à taper… Paris, Bali, Maldives"
        />
      </div>

      {/* Ville de départ */}
      <div className="space-y-3">
        <Label htmlFor="departure_city" className="label-eyebrow">Ville de départ</Label>
        <DestinationAutocomplete
          value={form.departure_city}
          onChange={(v) => setField("departure_city", v)}
          placeholder="Paris, Lyon, Marseille…"
        />
      </div>

      {/* Dates */}
      <div className="space-y-3">
        <Label className="label-eyebrow">Dates du séjour *</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="trigger-dates"
              className={cn(
                "w-full text-left luxury-input flex items-center gap-2",
                !range.from && "text-[#B7B2A8]"
              )}
            >
              <CalendarIcon className="h-4 w-4 text-[#96836B]" />
              {range.from
                ? range.to
                  ? `${format(range.from, "d MMM yyyy", { locale: fr })} → ${format(range.to, "d MMM yyyy", { locale: fr })}`
                  : format(range.from, "d MMM yyyy", { locale: fr })
                : "Sélectionnez vos dates"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-auto" align="start">
            <Calendar
              mode="range"
              selected={range}
              onSelect={(v) => setRange(v || { from: undefined, to: undefined })}
              numberOfMonths={2}
              locale={fr}
              data-testid="calendar-range"
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Adults */}
      <div className="space-y-3">
        <Label className="label-eyebrow">Adultes *</Label>
        <div className="flex items-center justify-between border-b border-[#EAE8E3] py-3">
          <div className="flex items-center gap-2 text-[#1A1B18]">
            <Users className="h-4 w-4 text-[#96836B]" />
            <span>{form.adults} adulte{form.adults > 1 ? "s" : ""}</span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" data-testid="btn-adults-minus" onClick={() => bumpAdults(-1)} className="w-8 h-8 border border-[#EAE8E3] hover:bg-[#1A1B18] hover:text-white transition-colors inline-flex items-center justify-center">
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button type="button" data-testid="btn-adults-plus" onClick={() => bumpAdults(1)} className="w-8 h-8 border border-[#EAE8E3] hover:bg-[#1A1B18] hover:text-white transition-colors inline-flex items-center justify-center">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Children */}
      <div className="space-y-3">
        <Label className="label-eyebrow">Enfants</Label>
        <div className="flex items-center justify-between border-b border-[#EAE8E3] py-3">
          <div className="flex items-center gap-2 text-[#1A1B18]">
            <Users className="h-4 w-4 text-[#96836B]" />
            <span>{form.children} enfant{form.children > 1 ? "s" : ""}</span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" data-testid="btn-children-minus" onClick={() => bumpKids(-1)} className="w-8 h-8 border border-[#EAE8E3] hover:bg-[#1A1B18] hover:text-white transition-colors inline-flex items-center justify-center">
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button type="button" data-testid="btn-children-plus" onClick={() => bumpKids(1)} className="w-8 h-8 border border-[#EAE8E3] hover:bg-[#1A1B18] hover:text-white transition-colors inline-flex items-center justify-center">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Budget */}
      <div className="space-y-3">
        <Label htmlFor="budget" className="label-eyebrow">Budget souhaité *</Label>
        <div className="flex items-center gap-2">
          <Input
            id="budget"
            data-testid="input-budget"
            type="number"
            min="0"
            step="100"
            placeholder="Ex. 3500"
            value={form.budget}
            onChange={(e) => setField("budget", e.target.value)}
            className="luxury-input"
          />
          <Select value={form.currency} onValueChange={(v) => setField("currency", v)}>
            <SelectTrigger data-testid="select-currency" className="w-24 border-0 border-b border-[#EAE8E3] rounded-none focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="CHF">CHF</SelectItem>
              <SelectItem value="MAD">MAD</SelectItem>
              <SelectItem value="XOF">XOF</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Trip type */}
      <div className="space-y-3">
        <Label className="label-eyebrow">Type de voyage</Label>
        <Select value={form.trip_type} onValueChange={(v) => setField("trip_type", v)}>
          <SelectTrigger data-testid="select-trip-type" className="luxury-input">
            <SelectValue placeholder="Optionnel" />
          </SelectTrigger>
          <SelectContent>
            {TRIP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Meal plan / Pension */}
      <div className="space-y-3 md:col-span-2">
        <Label className="label-eyebrow">Formule de pension</Label>
        <Select value={form.meal_plan} onValueChange={(v) => setField("meal_plan", v)}>
          <SelectTrigger data-testid="select-meal-plan" className="luxury-input">
            <SelectValue placeholder="Sélectionnez une formule (optionnel)" />
          </SelectTrigger>
          <SelectContent>
            {MEAL_PLANS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Flights */}
      <div className="space-y-3">
        <Label className="label-eyebrow">Vols</Label>
        <Select value={form.flights} onValueChange={(v) => setField("flights", v)}>
          <SelectTrigger data-testid="select-flights" className="luxury-input">
            <SelectValue placeholder="Optionnel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Avec vols">Avec vols</SelectItem>
            <SelectItem value="Sans vols">Sans vols</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transfers */}
      <div className="space-y-3">
        <Label className="label-eyebrow">Transferts</Label>
        <Select value={form.transfers} onValueChange={(v) => setField("transfers", v)}>
          <SelectTrigger data-testid="select-transfers" className="luxury-input">
            <SelectValue placeholder="Optionnel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Avec transferts">Avec transferts</SelectItem>
            <SelectItem value="Sans transferts">Sans transferts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2 border-t border-[#EAE8E3] pt-7 grid md:grid-cols-3 gap-x-10 gap-y-7">
        <div className="space-y-3">
          <Label htmlFor="full_name" className="label-eyebrow">Nom complet *</Label>
          <Input id="full_name" data-testid="input-fullname" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} className="luxury-input" placeholder="Votre nom" />
        </div>
        <div className="space-y-3">
          <Label htmlFor="email" className="label-eyebrow">Email *</Label>
          <Input id="email" data-testid="input-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="luxury-input" placeholder="vous@example.com" />
        </div>
        <div className="space-y-3">
          <Label htmlFor="phone" className="label-eyebrow">Téléphone</Label>
          <Input id="phone" data-testid="input-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="luxury-input" placeholder="+33 ..." />
        </div>
      </div>

      <div className="md:col-span-2 space-y-3">
        <Label htmlFor="message" className="label-eyebrow">Précisions (optionnel)</Label>
        <Textarea
          id="message"
          data-testid="input-message"
          rows={3}
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
          className="luxury-input resize-none"
          placeholder="Préférences, anniversaire, régime alimentaire, transferts privés…"
        />
      </div>

      <div className="md:col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-2">
        <p className="text-xs text-[#737373] max-w-md">
          En envoyant ce formulaire, vous acceptez d&apos;être contacté(e) par notre équipe.
          Aucune donnée n&apos;est partagée avec des tiers.
        </p>
        <Button
          type="submit"
          data-testid="btn-submit-quote"
          disabled={submitting}
          className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white px-10 py-6 text-[11px] uppercase tracking-[0.28em] transition-colors"
        >
          {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
        </Button>
      </div>
    </form>
  );
};

export default QuoteForm;
